'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useSession } from 'next-auth/react';
import { useTranslations } from '@/hooks/useTranslations';
import { useLocale } from '@/hooks/useLocale';
import type { IInvoice } from '@/types/invoice';
import type { IClient } from '@/types/client';
import { Loader } from 'lucide-react';
import { getSingleInvoice } from '@/actions/invoice/invoice-queries';
import { getClientById } from '@/actions/client/client-queries';
import { formatDateForInput } from '@/lib/date-formatter';
import {
  buildQrParamsFromSession,
  generatePaymentQrCodes,
  shouldShowPaymentQr,
  type PaymentQrResult,
} from '@/lib/helpers/payment-qr';

const PDFDownloadButton = dynamic(
  () =>
    import('@/features/invoices/download/pdf-download-button').then(
      (mod) => mod.PDFDownloadButton
    ),
  { ssr: false }
);

export const DownloadInvoice = ({
  children,
  invoiceId,
  version,
}: {
  children: React.ReactNode;
  invoiceId: string;
  // Changing this re-fetches the invoice, so an edit made while this
  // component stays mounted is picked up without a page reload.
  version?: string | number;
}) => {
  const t = useTranslations('InvoiceOverview');
  const locale = useLocale();
  const { data: session } = useSession();

  // The loaded data is stamped with the key it was fetched for, so a stale
  // result is never rendered and `ready` needs no separate reset on refetch.
  const loadKey = `${invoiceId}:${version ?? ''}`;
  const [loaded, setLoaded] = useState<{
    key: string;
    invoice: IInvoice | null;
    client: IClient | null;
    qrCodes: PaymentQrResult[];
    error: string | null;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      try {
        const inv = await getSingleInvoice(invoiceId);
        if (cancelled) return;

        const invoice = inv
          ? {
              ...inv,
              items: inv.items.map((item) => ({
                ...item,
                price: +item.price,
                qty: +item.qty,
                tax: +item.tax,
              })),
            }
          : null;

        let client: IClient | null = null;
        if (invoice?.clientId) {
          try {
            client = (await getClientById(invoice.clientId)) ?? null;
            if (cancelled) return;
          } catch (error) {
            console.error('Error fetching client:', error);
            if (cancelled) return;
          }
        }

        // Generate payment QR codes (country-specific)
        let qrCodes: PaymentQrResult[] = [];
        if (invoice && shouldShowPaymentQr(session)) {
          qrCodes = await generatePaymentQrCodes(
            buildQrParamsFromSession(session!, invoice)
          );
          if (cancelled) return;
        }

        setLoaded({ key: loadKey, invoice, client, qrCodes, error: null });
      } catch (err) {
        console.error('Failed to load invoice for download:', err);
        if (cancelled) return;
        setLoaded({
          key: loadKey,
          invoice: null,
          client: null,
          qrCodes: [],
          error: t('downloadError') || 'Failed to load invoice',
        });
      }
    };

    loadData();

    return () => {
      cancelled = true;
    };
  }, [invoiceId, loadKey]);

  const ready = loaded?.key === loadKey;
  const invoice = ready ? loaded.invoice : null;
  const client = ready ? loaded.client : null;
  const qrCodes = ready ? loaded.qrCodes : [];
  const error = ready ? loaded.error : null;

  if (error) {
    return (
      <div className="flex items-center px-2 py-1.5 gap-2">
        <span className="text-sm font-medium">{error}</span>
      </div>
    );
  }

  if (!session || !invoice || !ready)
    return (
      <div className="flex items-center px-2 py-1.5 gap-2">
        <Loader className="w-4 h-4 animate-spin" color="#787B80" />
        <span className="text-sm font-medium">Loading</span>
      </div>
    );

  const fileName =
    `${invoice.invoiceType}_${invoice.invoiceNumber}_${
      client?.name || 'Unknown'
    }_${formatDateForInput(new Date(invoice.issuedDate))}`
      .replace(/[^a-z0-9\-_]/gi, '_')
      .trim() + '.pdf';

  return (
    <PDFDownloadButton
      invoice={invoice}
      client={client}
      session={session}
      fileName={fileName}
      qrCodes={qrCodes}
      locale={locale}
      translations={{
        no: t('no'),
        invoiceFrom: t('invoiceFrom'),
        taxId: t('taxId'),
        invoiceTo: t('invoiceTo'),
        registrationNumber: t('registrationNumber'),
        invoiceNumber: t('invoiceNumber'),
        issuedAt: t('issuedAt'),
        fromDate: t('fromDate'),
        dueDate: t('dueDate'),
        bankName: t('bankName'),
        account: t('account'),
        bic: t('bic'),
        email: t('email'),
        items: t('items'),
        cost: t('cost'),
        qty: t('qty'),
        unit: t('unit'),
        price: t('price'),
        subtotal: t('subtotal'),
        tax: t('tax'),
        total: t('total'),
        vatNotice: t('vatNotice'),
        issuedBy: t('issuedBy'),
        receivedBy: t('receivedBy'),
        scanToPay: t('scanToPay'),
      }}
    >
      {children}
    </PDFDownloadButton>
  );
};
